/**
 * Soft≠Live · Command desk — ambient collab (not "AI brand" chrome).
 * Mount: /ops/workbench · map = world · panel = next actions + hold.
 *
 * Live (production Page186):
 *   GET /ops/workbench/status   posture multi-plane
 *   GET /devices                plant_plane
 *   GET /zones                  rings_plane
 *   GET /responder-dispatches(|/mine) missions_plane
 *   GET /alarms/summary         alerts_plane
 * Soft theatre · 127 only · never invents Live video or remote holds.
 *
 * v561 — Run Live multi-bus · soft operator copy.
 */
import { useMemo, useState } from "react";
import { html, Ico, go, M, _SPRING, SOFT, Alert } from "./ui.js";
import { path, useApi } from "./api.js";
import { isLocalHost } from "./pathseal.js";
import { softCommandWorkbenchOf } from "./soft-command-loop.js";
import { SoftDispatchBoard } from "./soft-dispatch-board.js";
import {
  softZoneCatalogOf,
  softZoneRunScopeOf,
  softZoneVolumeVmOf,
  loadActiveZoneId,
} from "./ops-zone-fabric.js";
import { overviewDevicesOf } from "./ops-overview-desk.js";
import { liveZonesOf } from "./ops-zone-ops-desk.js";
import { operatorSay } from "./operator-say.js";

const OPEN_MISSION = new Set([
  "pending", "accepted", "en_route", "enroute", "on_scene", "onscene", "assigned", "active",
]);

const LIVE_RAILS = [
  { id: "jobs", label: "Jobs", long: "Open runs" },
  { id: "plant", label: "Plant", long: "Site plant" },
  { id: "rings", label: "Rings", long: "Map volumes" },
];

/** Soft≠Live: missing OpenAPI op must not crash the desk. */
function safePath(op, opts) {
  try {
    return path(op, opts);
  } catch {
    if (op === "get__ops_workbench_status") return "/ops/workbench/status";
    if (op === "get__ops_dispatch_status") return "/ops/dispatch/status";
    if (op === "get__ops_alarms_status") return "/ops/alarms/status";
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length ? `/devices?${parts.join("&")}` : "/devices";
    }
    if (op === "get__zones") return "/zones";
    if (op === "get__alarms_summary") return "/alarms/summary";
    if (op === "get__responder-dispatches") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length
        ? `/responder-dispatches?${parts.join("&")}`
        : "/responder-dispatches";
    }
    if (op === "get__responder-dispatches_mine") {
      const q = (opts && opts.query) || {};
      const parts = Object.entries(q).map(([k, v]) => `${k}=${encodeURIComponent(v)}`);
      return parts.length
        ? `/responder-dispatches/mine?${parts.join("&")}`
        : "/responder-dispatches/mine";
    }
    return "";
  }
}

/** Operator-facing bus errors — never paths or stacks. */
export function softRunErrorOf(err) {
  const n = String(err == null ? "" : err).trim().toLowerCase();
  if (!n) return "Run board could not be loaded.";
  if (/\b403\b|forbidden|insufficient|not_authorized|scope/.test(n)) {
    return "Your account cannot open that board. Ask a site manager if you need access.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b404\b|not found/.test(n)) {
    return "Run status is not available on this console yet.";
  }
  if (/\b429\b|too many/.test(n)) {
    return "Too many tries — wait a moment, then retry.";
  }
  if (/network|failed to fetch|timeout|aborted/.test(n)) {
    return "Network issue — try Refresh.";
  }
  if (/\/ops\/|\/devices|\/zones|\/alarms|\/responder|http|get |post |status_path|sql|traceback/.test(n)) {
    return "Run board could not be loaded.";
  }
  return "Run board could not be loaded.";
}

export function softRunSeatLabelOf(role) {
  const r = String(role || "").toLowerCase();
  if (r === "user") return "Operator";
  if (r === "admin") return "Site manager";
  if (r === "superadmin") return "Platform";
  if (r === "sa_staff") return "Staff";
  return role || "Seat";
}

/** Pure: seat from Live workbench posture (Page186). */
export function opsWorkbenchSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const plantMax = Number(lim.plant_paint_max);
  const missionMax = Number(lim.missions_paint_max);
  const alertMax = Number(lim.alert_paint_max);
  const zoneMax = Number(lim.zone_paint_max);
  const plant = (p.plant_plane_allowed === true || p.register_allowed === true);
  const rings = (p.rings_plane_allowed === true);
  const alerts = (p.alerts_plane_allowed === true);
  const missionsFleet = (p.missions_fleet_allowed === true);
  const missionsMine = (p.missions_mine_allowed === true);
  const missions = p.missions_plane_allowed != null
    ? (p.missions_plane_allowed === true)
    : (missionsFleet || missionsMine);
  return {
    enabled: (p.enabled === true),
    armed: (p.armed === true),
    plantAllowed: plant,
    ringsAllowed: rings,
    alertsAllowed: alerts,
    missionsAllowed: missions,
    missionsFleetAllowed: missionsFleet,
    missionsMineAllowed: missionsMine,
    registerAllowed: plant,
    reReadAllowed: (p.re_read_allowed === true),
    fieldHoldAllowed: (p.field_hold_allowed === true),
    softTheatreLocalOnly: (p.soft_theatre_local_only === true),
    plantPaintMax: Number.isFinite(plantMax) && plantMax > 0 ? Math.floor(plantMax) : 200,
    missionsPaintMax: Number.isFinite(missionMax) && missionMax > 0 ? Math.floor(missionMax) : 40,
    alertPaintMax: Number.isFinite(alertMax) && alertMax > 0 ? Math.floor(alertMax) : 40,
    zonePaintMax: Number.isFinite(zoneMax) && zoneMax > 0 ? Math.floor(zoneMax) : 80,
    role: p.principal_role || null,
    detail: p.detail || null,
  };
}

export function liveMissionsOf(pack, paintMax) {
  const rows = (pack && (pack.items || pack.dispatches || pack.missions))
    || (Array.isArray(pack) ? pack : []);
  const max = Number.isFinite(Number(paintMax)) && Number(paintMax) > 0
    ? Math.floor(Number(paintMax))
    : 40;
  return rows.slice(0, max).map((m, i) => {
    const st = String(m.status || m.state || "").trim().toLowerCase();
    const open = OPEN_MISSION.has(st) || (!st && !m.resolved_at && !m.closed_at);
    return {
      id: String(m.dispatch_id || m.id || ("m" + i)),
      status: st || "open",
      open,
      device: String(m.device_id || m.target_device_id || "").trim(),
      who: String(m.assignee || m.personnel_id || m.responder_id || m.owner_admin || "").trim(),
      summary: String(m.objective || m.note || m.reason || m.detail || "Run").trim().slice(0, 90),
    };
  });
}

export function liveAlertSummaryOf(pack) {
  const p = pack && typeof pack === "object" ? pack : {};
  return {
    d24: Number(p.last_24h != null ? p.last_24h : p.open_24h != null ? p.open_24h : 0) || 0,
    d7: Number(p.last_7d != null ? p.last_7d : 0) || 0,
  };
}

/** Live Run · production: multi-plane BE buses · never Soft fleet invent. */
function LiveRunBridge({ toast, activeZoneId, liveZones, devices: devicesProp }) {
  const [rail, setRail] = useState("jobs");
  const posture = useApi(safePath("get__ops_workbench_status"));
  const post = posture.data || {};
  const seat = opsWorkbenchSeatOf(
    posture.loading || !posture.data
      ? {
          plant_plane_allowed: false,
          rings_plane_allowed: false,
          alerts_plane_allowed: false,
          missions_plane_allowed: false,
          missions_fleet_allowed: false,
          missions_mine_allowed: false,
          re_read_allowed: false,
          soft_theatre_local_only: true,
          limits: {
            plant_paint_max: 200,
            missions_paint_max: 40,
            alert_paint_max: 40,
            zone_paint_max: 80,
          },
        }
      : post,
  );

  const devicesR = useApi(
    seat.plantAllowed
      ? safePath("get__devices", { query: { limit: seat.plantPaintMax } })
      : "",
  );
  const zonesR = useApi(seat.ringsAllowed ? safePath("get__zones") : "");
  const missionPath = seat.missionsFleetAllowed
    ? safePath("get__responder-dispatches", {
      query: { limit: seat.missionsPaintMax },
    })
    : (seat.missionsMineAllowed
      ? safePath("get__responder-dispatches_mine", {
        query: { limit: seat.missionsPaintMax },
      })
      : "");
  const missionsR = useApi(seat.missionsAllowed ? missionPath : "");
  const summaryR = useApi(
    seat.alertsAllowed ? safePath("get__alarms_summary") : "",
  );

  const fromApi = useMemo(() => overviewDevicesOf(devicesR.data), [devicesR.data]);
  const devices = useMemo(() => {
    if (devicesR.data || devicesR.error) return fromApi;
    return Array.isArray(devicesProp) ? devicesProp : [];
  }, [devicesR.data, devicesR.error, fromApi, devicesProp]);

  const zonesFromBus = useMemo(() => liveZonesOf(zonesR.data), [zonesR.data]);
  const zones = useMemo(() => {
    if (zonesR.data || zonesR.error) {
      return zonesFromBus.slice(0, seat.zonePaintMax);
    }
    const parent = Array.isArray(liveZones) ? liveZones : [];
    return parent.slice(0, seat.zonePaintMax).map((z) => ({
      id: z.zone_id || z.id || z.label,
      label: z.label || z.id,
      soft: !!z.soft,
      count: z.count || z.device_count || 0,
      deviceIds: z.deviceIds || z.device_ids || [],
    }));
  }, [zonesR.data, zonesR.error, zonesFromBus, liveZones, seat.zonePaintMax]);

  const missions = useMemo(
    () => liveMissionsOf(missionsR.data, seat.missionsPaintMax),
    [missionsR.data, seat.missionsPaintMax],
  );
  const openMissions = missions.filter((m) => m.open);
  const alerts = useMemo(() => liveAlertSummaryOf(summaryR.data), [summaryR.data]);

  const online = devices.filter((d) => d && !d.offline).length;
  const offline = devices.filter((d) => d && d.offline).length;
  const zonesN = zones.filter((z) => z && !z.soft).length;
  const zoneCue = String(activeZoneId || "").trim();
  const seatLabel = softRunSeatLabelOf(seat.role || post.principal_role);

  const busErr = [devicesR.error, zonesR.error, missionsR.error, summaryR.error]
    .filter(Boolean)
    .find((e) => !String(e).match(/\b404\b/))
    || (posture.error && !String(posture.error).match(/\b404\b/) ? posture.error : "");

  const sealed = !posture.loading
    && !seat.plantAllowed
    && !seat.missionsAllowed
    && !seat.alertsAllowed
    && !seat.ringsAllowed;

  const reRead = () => {
    if (!seat.reReadAllowed && !posture.loading) {
      toast && toast("Refresh is closed for this seat", "warn");
      return;
    }
    posture.reload && posture.reload();
    devicesR.reload && devicesR.reload();
    zonesR.reload && zonesR.reload();
    missionsR.reload && missionsR.reload();
    summaryR.reload && summaryR.reload();
    toast && toast("Run board refreshed", "go");
  };

  const pulse = posture.loading
    ? "Loading Run board…"
    : sealed
      ? "This seat cannot run site jobs"
      : openMissions.length
        ? `${openMissions.length} open run${openMissions.length === 1 ? "" : "s"} · ${online} plant on site`
        : devices.length
          ? `${online} live · ${offline} quiet · ${zonesN} volume${zonesN === 1 ? "" : "s"}`
          : seat.plantAllowed
            ? "Plant quiet for this account · no open runs"
            : "Plant board sealed";

  const focusZone = zones.find(
    (z) => z && (z.id === zoneCue || z.label === zoneCue || z.zone_id === zoneCue),
  );

  return html`<div
    className="ops-hud ops-run ops-coc ops-coc--flat ops-run-flat"
    data-cmc-residual="command-os-workbench-live"
    data-state=${sealed ? "sealed" : "live"}
    data-soft="0"
    data-page="page186"
    data-field="v561"
    data-rail=${rail}>
    <header className="ops-run__mast ops-run-flat__head">
      <div className="ops-run__id">
        <p className="ops-run__k">Run</p>
        <h2 className="ops-run__title">Jobs pulse</h2>
      </div>
      <div className="ops-hud__top-actions">
        <span className="ops-hud__chip ops-hud__chip--seat" title="Seat">${seatLabel}</span>
        <button type="button" className="ops-hud__refresh" title="Refresh"
          disabled=${!seat.reReadAllowed || posture.loading}
          onClick=${reRead}>
          <${Ico} d="refresh" w=${13}/>
        </button>
      </div>
    </header>
    <p className="ops-run__pulse ops-run-flat__pulse">${pulse}</p>
    ${zoneCue
      ? html`<p className="ops-run__lede ops-run__sub">
          Focus volume · ${focusZone ? (focusZone.label || zoneCue) : zoneCue}
        </p>`
      : html`<p className="ops-run__lede ops-run__sub">Whole site · open a volume to narrow focus</p>`}

    ${busErr
      ? html`<${Alert} tone="warn" say=${softRunErrorOf(busErr)}/>`
      : null}
    ${sealed
      ? html`<${Alert} tone="warn" say="Run is closed for this seat."/>`
      : null}

    <div className="ops-hud__meters ops-run__meters ops-run-flat__stats" role="group" aria-label="Site counts">
      <div className="ops-hud__meter ops-run-flat__stat" data-on=${online ? "1" : "0"} data-tone="go">
        <b>${seat.plantAllowed ? online : "—"}</b><em>Online</em>
      </div>
      <div className="ops-hud__meter ops-run-flat__stat" data-on=${openMissions.length ? "1" : "0"} data-tone="warn">
        <b>${seat.missionsAllowed ? openMissions.length : "—"}</b><em>Runs</em>
      </div>
      <div className="ops-hud__meter ops-run-flat__stat" data-on=${zonesN ? "1" : "0"} data-tone="go">
        <b>${seat.ringsAllowed ? zonesN : "—"}</b><em>Volumes</em>
      </div>
      <div className="ops-hud__meter ops-run-flat__stat" data-on=${alerts.d24 ? "1" : "0"} data-tone="warn">
        <b>${seat.alertsAllowed ? alerts.d24 : "—"}</b><em>24h</em>
      </div>
    </div>

    <div className="ops-run__cmd ops-run-flat__cmd ops-coc__command">
      <button type="button" className="btn btn--go" onClick=${() => go("/ops/dispatch")}
        disabled=${sealed}
        title="Open Send board">
        Open Send
      </button>
      <button type="button" className="btn btn--ghost" onClick=${() => go("/ops/devices")}>
        Plant
      </button>
      <button type="button" className="btn btn--ghost" onClick=${() => go("/ops/zones")}>
        Volumes
      </button>
      <button type="button" className="btn btn--ghost" onClick=${() => go("/ops/alarms")}>
        Alerts
      </button>
    </div>

    <nav className="ops-seg ops-run__tabs ops-run-flat__tabs" role="tablist" aria-label="Run sections">
      ${LIVE_RAILS.map((r) => html`
        <button key=${r.id} type="button" role="tab" className="ops-seg__btn"
          data-on=${rail === r.id ? "1" : "0"}
          title=${r.long}
          onClick=${() => setRail(r.id)}>${r.label}</button>`)}
    </nav>

    <div className="ops-run__body ops-coc__body">
      ${rail === "jobs" ? html`
        <section className="ops-run__pane" aria-label="Jobs">
          <div className="ops-hud__lane-h">
            <p className="ops-hud__k"><s></s>${seat.missionsFleetAllowed ? "Open runs · site" : "My runs"}</p>
            <em className="ops-run__match">${openMissions.length || 0}</em>
          </div>
          ${!seat.missionsAllowed
            ? html`<p className="ops-hud__quiet">Runs board is closed for this seat.</p>`
            : missionsR.loading
              ? html`<p className="ops-hud__quiet">Loading runs…</p>`
              : !openMissions.length
                ? html`<div className="ops-hud__quiet">
                    <b>No open runs</b>
                    · Use Send when someone is on roster.
                    <button type="button" className="btn btn--sm" style=${{ marginTop: 8 }}
                      onClick=${() => go("/ops/dispatch")}>Open Send</button>
                  </div>`
                : html`<div className="ops-run__stack" role="list">
                    ${openMissions.slice(0, 12).map((m) => html`
                      <article key=${m.id} className="ops-run__row ops-coc__card" role="listitem">
                        <span className="ops-run__main">
                          <b>${m.summary || m.id}</b>
                          <u>${m.device || "—"} · ${m.who || "unassigned"}</u>
                        </span>
                        <em className="ops-run__trail">${m.status}</em>
                      </article>`)}
                  </div>`}
          ${seat.alertsAllowed
            ? html`<p className="ops-vols__note" style=${{ marginTop: 10 }}>
                Alerts last 24h: <b>${alerts.d24}</b>
                · 7d: <b>${alerts.d7}</b>
                <button type="button" className="btn btn--text btn--xs" style=${{ marginLeft: 8 }}
                  onClick=${() => go("/ops/alarms")}>View</button>
              </p>`
            : null}
        </section>
      ` : null}

      ${rail === "plant" ? html`
        <section className="ops-run__pane" aria-label="Plant">
          <div className="ops-hud__lane-h">
            <p className="ops-hud__k"><s></s>Plant</p>
            <em className="ops-run__match">${devices.length || 0}</em>
          </div>
          ${!seat.plantAllowed
            ? html`<p className="ops-hud__quiet">Plant is closed for this seat.</p>`
            : devicesR.loading
              ? html`<p className="ops-hud__quiet">Loading plant…</p>`
              : !devices.length
                ? html`<p className="ops-hud__quiet"><b>No devices</b> for this account yet.</p>`
                : html`<div className="ops-vols__chips" role="list">
                    ${devices.slice(0, 16).map((d) => html`
                      <span key=${d.id} className="ops-vols__chip" data-on=${d.offline ? "0" : "1"} role="listitem">
                        <b title=${d.id}>${d.label || d.id}</b>
                        <i>${d.offline ? "quiet" : "live"}</i>
                      </span>`)}
                  </div>`}
          <button type="button" className="btn btn--sm btn--ghost" style=${{ marginTop: 10 }}
            onClick=${() => go("/ops/devices")}>Full plant</button>
        </section>
      ` : null}

      ${rail === "rings" ? html`
        <section className="ops-run__pane" aria-label="Rings">
          <div className="ops-hud__lane-h">
            <p className="ops-hud__k"><s></s>Volumes</p>
            <em className="ops-run__match">${zonesN || 0}</em>
          </div>
          ${!seat.ringsAllowed
            ? html`<p className="ops-hud__quiet">Volumes are closed for this seat.</p>`
            : zonesR.loading
              ? html`<p className="ops-hud__quiet">Loading volumes…</p>`
              : !zonesN
                ? html`<div className="ops-hud__quiet">
                    <b>No map rings yet</b>
                    · Site manager draws them on map survey.
                    <button type="button" className="btn btn--sm" style=${{ marginTop: 8 }}
                      onClick=${() => go("/ops/zones")}>Open Volumes</button>
                  </div>`
                : html`<div className="ops-run__stack" role="list">
                    ${zones.filter((z) => z && !z.soft).slice(0, 12).map((z) => html`
                      <article key=${z.id} className="ops-run__row ops-coc__card" role="listitem"
                        data-on=${zoneCue && (z.id === zoneCue || z.label === zoneCue) ? "1" : "0"}>
                        <span className="ops-run__main">
                          <b>${z.label || z.id}</b>
                          <u>${(z.deviceIds && z.deviceIds.length) || z.count || 0} plant in ring</u>
                        </span>
                        <em className="ops-run__trail">on site</em>
                      </article>`)}
                  </div>`}
        </section>
      ` : null}
    </div>

    <p className="ops-hud__quiet">
      ${sealed
        ? "This account cannot run site jobs · ask a site manager if you need access."
        : "Plant and runs are on for your organisation. Camera practice covers and Hold-all stay in the lab workspace only."}
    </p>

    <footer className="ops-hud__foot ops-run__foot">
      <span className="ops-hud__foot-role" data-soft="0">site boards</span>
    </footer>
  </div>`;
}

const RAILS = [
  { id: "run", label: "Jobs" },
  { id: "watch", label: "Watch" },
  { id: "drag", label: "Send" },
  { id: "log", label: "Trace" },
];

/**
 * @param {{ fleet:any, toast?:function, activeZoneId?:string, liveZones?:any[], devices?:any[] }} props
 */
export function OpsCommandWorkbench({ fleet, toast, activeZoneId, liveZones, devices }) {
  const local = isLocalHost();
  const enabled = !!(local && fleet && fleet.enabled && fleet.state);
  const [rail, setRail] = useState("run");
  const zoneId = String(activeZoneId || loadActiveZoneId() || "").trim();

  const zoneScope = useMemo(() => {
    if (!enabled || !zoneId) return null;
    const cat = softZoneCatalogOf({
      zones: Array.isArray(liveZones) ? liveZones : [],
      devices: Array.isArray(devices) ? devices : [],
      softState: fleet.state,
      centre: fleet.state && fleet.state.origin,
    });
    const z =
      cat.find((x) => x.id === zoneId || x.label === zoneId) ||
      { id: zoneId, label: zoneId, cameraIds: [] };
    return softZoneRunScopeOf(z, fleet.state, { radiusM: 95 });
  }, [enabled, zoneId, fleet && fleet.state, liveZones, devices]);

  const zoneVm = useMemo(() => {
    if (!zoneScope || !zoneScope.ok) return null;
    return softZoneVolumeVmOf(
      {
        id: zoneScope.zoneId,
        label: zoneScope.label,
        soft: zoneScope.soft,
        cameraIds: zoneScope.cameraIds,
        ll: zoneScope.origin,
      },
      enabled ? fleet.state : null,
      Array.isArray(devices) ? devices : [],
    );
  }, [zoneScope, enabled, fleet && fleet.state, devices]);

  const wb = softCommandWorkbenchOf(enabled ? fleet.state : null, {
    local,
    livePlayerWired: false,
    zoneScope,
  });

  const stopAll = () => {
    if (!fleet || !fleet.humanStopAll) return;
    fleet.humanStopAll("Held · all motion · operator");
    toast && toast("All motion held", "warn");
  };
  const stopZone = () => {
    if (!fleet || !zoneScope || !zoneScope.origin) {
      stopAll();
      return;
    }
    if (fleet.humanStopInZone) {
      fleet.humanStopInZone(
        zoneScope.origin,
        zoneScope.radiusM,
        `Held · ${zoneScope.label}`,
      );
      toast && toast(`Held · ${zoneScope.label}`, "warn");
      return;
    }
    stopAll();
  };
  const resume = () => {
    if (!fleet || !fleet.humanResumeAi) return;
    fleet.humanResumeAi();
    toast && toast("Automation continues", "go");
  };
  const stopOne = (id) => {
    if (!fleet || !fleet.humanStopDispatch) return;
    fleet.humanStopDispatch(id);
    toast && toast("Job held", "warn");
  };
  const redirectOne = (id) => {
    if (!fleet || !fleet.humanRedirect) return;
    fleet.humanRedirect(id, "Observe only");
    toast && toast("Redirected · observe", "go");
  };
  const snapCam = (camId) => {
    if (!fleet || !fleet.requestSnapshot) return;
    fleet.requestSnapshot(camId);
    toast && toast("Snapshot queued", "go");
  };
  const focusUnit = (id) => {
    if (!id) return;
    try {
      window.dispatchEvent(new CustomEvent("croc-soft-focus", { detail: { id } }));
    } catch {
      /* */
    }
  };
  const frameVolume = () => {
    if (!zoneId) return;
    try {
      window.dispatchEvent(new CustomEvent("croc-zone-frame", { detail: { zoneId } }));
    } catch {
      /* */
    }
  };

  if (!local) {
    return html`<${LiveRunBridge}
      toast=${toast}
      activeZoneId=${activeZoneId}
      liveZones=${liveZones}
      devices=${devices}/>`;
  }

  if (!enabled) {
    return html`<div
      className="ops-hud ops-run ops-coc ops-coc--flat ops-run-flat"
      data-cmc-residual="command-os-workbench"
      data-state="off"
      data-field="v554">
      <header className="ops-run__mast ops-run-flat__head">
        <div className="ops-run__id">
          <p className="ops-run__k">Run · practice</p>
          <h2 className="ops-run__title">Run</h2>
        </div>
        <span className="ops-run__mode" data-held="1">off</span>
      </header>
      <p className="ops-run__pulse ops-run-flat__pulse">Practice fleet off</p>
      <p className="ops-hud__quiet">Map layers · enable practice fleet · jobs and watch light up here.</p>
    </div>`;
  }

  const k = wb.kpis || {};
  const sup = wb.supervisor || {};
  const human = sup.mode === "human" || sup.paused;
  const observe = wb.observe || {};
  const multi = (observe.multiView && observe.multiView.cameras) || [];
  const trigger = observe.spatialTrigger || {};
  const hits = trigger.hits || [];
  const jobs = wb.dispatches || [];
  const incs = wb.incidents || [];
  const log = wb.evidence || [];
  const z = wb.zone;
  const ready = (zoneVm && zoneVm.ready) || "open";

  const movingN = jobs.filter((d) => d.status === "EN_ROUTE" || d.status === "ARRIVED").length;
  const statusLine = human
    ? "Held · on you"
    : movingN
      ? `${movingN} under way`
      : jobs.length
        ? `${jobs.length} job${jobs.length === 1 ? "" : "s"}`
        : multi.length
          ? `${multi.length} cover${multi.length === 1 ? "" : "s"}`
          : z
            ? "Focus armed"
            : "Quiet";

  const subLine = human
    ? sup.reason || "Automation paused"
    : z
      ? `~${z.radiusM || 95}m · ring active`
      : multi.length
        ? `${hits.length} unit${hits.length === 1 ? "" : "s"} in view`
        : "Map is space · command is next move";

  const next = human
    ? { label: "Continue", run: resume, tone: "go" }
    : jobs.length
      ? {
          label: z ? "Hold zone" : "Hold all",
          run: z ? stopZone : stopAll,
          tone: "warn",
        }
      : multi.length
        ? { label: "Open Watch", run: () => setRail("watch"), tone: "" }
        : { label: "Send unit", run: () => setRail("drag"), tone: "" };

  const jobsN = k.dispatches || jobs.length || 0;
  const openN = k.incidents || incs.length || 0;
  const coversN = multi.length;
  const nearN = hits.length;
  const paneTitle =
    rail === "run"
      ? z
        ? "Jobs · volume"
        : "Jobs"
      : rail === "watch"
        ? z
          ? "Covers · volume"
          : "Covers"
        : rail === "drag"
          ? "Send"
          : "Trace";

  return html`<div
    className="ops-hud ops-run ops-field ops-field--run ops-coc ops-coc--flat ops-run-flat"
    data-cmc-residual="command-os-workbench"
    data-desk="run"
    data-supervisor=${human ? "human" : "ai"}
    data-soft="1"
    data-rail=${rail}
    data-held=${human ? "1" : "0"}
    data-zone=${z ? z.id : ""}
    data-ready=${z ? ready : ""}
    data-field="v554"
    data-field-legacy="v541">

    <div className="ops-hud__scan" aria-hidden="true" />

    <header className="ops-run__mast ops-run-flat__head" aria-label="Run desk">
      <div className="ops-run__id">
        <p className="ops-run__k">Run · practice</p>
        <div className="ops-run__title-row">
          <h2 className="ops-run__title">${statusLine}</h2>
          ${jobsN
            ? html`<span className="ops-run__count" title="Active jobs">${jobsN}</span>`
            : null}
        </div>
      </div>
      <span
        className="ops-run__mode"
        data-held=${human ? "1" : "0"}
        title=${human ? "Automation held" : "Practice workspace · this host only"}>
        ${human ? "held" : "practice"}
      </span>
    </header>

    <p className="ops-run__lede ops-run__sub" title="Run scope detail">${subLine}</p>

    <button
      type="button"
      className="ops-run__scope ops-run-flat__scope"
      data-on=${z ? "1" : "0"}
      data-ready=${z ? ready : ""}
      title=${z ? "Frame this volume on the map" : "Open volumes"}
      onClick=${() => (z ? frameVolume() : go("/ops/zones"))}>
      <span className="ops-run__scope-main ops-run-flat__scope-main">
        <b>${z ? z.label : "Whole site"}</b>
        <u>${z
          ? `${(zoneVm && zoneVm.readyLabel) || "Open"} · ${z.cams || 0} cams · ${z.units || 0} near`
          : "Pick a volume on the map"}</u>
      </span>
      <em>${z ? "Frame" : "Volumes"}</em>
    </button>

    <div className="ops-run__cmd ops-run-flat__cmd ops-coc__command" data-held=${human ? "1" : "0"}>
      <button type="button" className="btn btn--danger" onClick=${stopAll} title="Hold all motion">
        <${Ico} d="siren" w=${13}/>
        <span>STOP ALL</span>
      </button>
      <button
        type="button"
        className=${"btn" + (next.tone === "go" ? " btn--go" : next.tone === "warn" ? " btn--danger" : " btn--ghost") + " ops-hud__cta"}
        data-tone=${next.tone || "neutral"}
        disabled=${next.tone === "go" && !human}
        onClick=${() => next.run && next.run()}>
        ${next.label}
      </button>
    </div>

    <div className="ops-hud__meters ops-run__meters ops-run-flat__stats" role="group" aria-label="Quick counts">
      <button type="button" className="ops-hud__meter ops-run-flat__stat" data-on=${rail === "run" || jobsN ? "1" : "0"} data-tone="go"
        onClick=${() => setRail("run")}>
        <b>${jobsN}</b><em>Jobs</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${jobsN ? Math.min(1, Math.max(0.2, jobsN / 6)) : 0.18})` }}/>
      </button>
      <div className="ops-hud__meter ops-run-flat__stat" data-on=${openN ? "1" : "0"} data-tone="warn">
        <b>${openN}</b><em>Open</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${openN ? 1 : 0.18})` }}/>
      </div>
      <button type="button" className="ops-hud__meter ops-run-flat__stat" data-on=${rail === "watch" || coversN ? "1" : "0"} data-tone="go"
        onClick=${() => setRail("watch")}>
        <b>${coversN}</b><em>Covers</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${coversN ? Math.min(1, Math.max(0.2, coversN / 6)) : 0.18})` }}/>
      </button>
      <div className="ops-hud__meter ops-run-flat__stat" data-on=${nearN ? "1" : "0"} data-tone="go">
        <b>${nearN}</b><em>Near</em>
        <s className="ops-hud__meter-bar" style=${{ transform: `scaleX(${nearN ? 1 : 0.18})` }}/>
      </div>
    </div>

    <nav className="ops-seg ops-run__tabs ops-run-flat__tabs ops-run__segs" role="tablist" aria-label="Command sections">
      ${RAILS.map(
        (r) => html`
          <button
            key=${r.id}
            type="button"
            role="tab"
            className="ops-seg__btn ops-run-flat__tab"
            data-on=${rail === r.id ? "1" : "0"}
            aria-selected=${rail === r.id ? "true" : "false"}
            onClick=${() => setRail(r.id)}>
            ${r.label}
          </button>`,
      )}
    </nav>

    <div className="ops-run__body ops-coc__body">
      ${rail === "run"
        ? html`<${M.section}
            key="pane-run"
            className="ops-run__pane ops-coc__pane"
            aria-label="Jobs"
            initial=${{ opacity: 0, y: 6 }}
            animate=${{ opacity: 1, y: 0 }}
            transition=${SOFT}>
            <div className="ops-hud__lane-h">
              <p className="ops-hud__k"><s></s>${paneTitle}</p>
              <em className="ops-run__match">${jobsN}</em>
            </div>
            <div className="ops-run__stack ops-coc__stack" role="list">
              ${jobs.length
                ? jobs.map(
                    (d, i) => html`
                      <${M.article}
                        key=${d.id}
                        className="ops-run__row ops-coc__card ops-coc__card--field"
                        role="listitem"
                        data-st=${d.status}
                        data-ai=${d.aiControlled ? "1" : "0"}
                        data-zone=${z ? "1" : "0"}
                        initial=${{ opacity: 0, y: 5 }}
                        animate=${{ opacity: 1, y: 0 }}
                        transition=${{ ...SOFT, delay: Math.min(0.1, i * 0.03) }}>
                        <span className="ops-run__main">
                          <b>${d.objective || d.id}</b>
                          <u>
                            ${d.destination && d.destination.place
                              ? d.destination.place
                              : "Map target"}
                            · ${d.humanOverride ? "you" : "auto"}
                            · ${(d.deviceIds || []).join(" · ") || "—"}
                          </u>
                        </span>
                        <em className="ops-run__trail" data-st=${d.status}>${prettyStatus(d.status)}</em>
                        <div className="ops-run__acts ops-coc__acts">
                          <button type="button" className="btn btn--xs btn--ghost"
                            disabled=${!(d.deviceIds && d.deviceIds[0])}
                            onClick=${() => focusUnit(d.deviceIds && d.deviceIds[0])}>Map</button>
                          <button type="button" className="btn btn--xs btn--ghost"
                            disabled=${d.status === "OVERRIDDEN"}
                            onClick=${() => redirectOne(d.id)}>Redirect</button>
                          <button type="button" className="btn btn--xs ops-coc__hold-one"
                            disabled=${d.status === "OVERRIDDEN"}
                            onClick=${() => stopOne(d.id)}>Hold</button>
                        </div>
                      <//>`,
                  )
                : html`<div className="ops-hud__quiet ops-coc__empty ops-coc__empty--field">
                    <b>No jobs</b> · Send a unit or wait for activity near focus.
                    <button type="button" className="btn btn--sm" style=${{ marginTop: 8 }}
                      onClick=${() => setRail("drag")}>Send unit</button>
                  </div>`}
            </div>

            ${incs.length
              ? html`
                <div className="ops-hud__lane-h" style=${{ marginTop: 10 }}>
                  <p className="ops-hud__k"><s></s>Open</p>
                  <em className="ops-run__match">${openN}</em>
                </div>
                <div className="ops-run__stack" role="list">
                  ${incs.map(
                    (inc) => html`
                      <article key=${inc.id} className="ops-run__row ops-coc__card ops-coc__card--inc" role="listitem">
                        <span className="ops-run__main">
                          <b>${inc.title}</b>
                          <u>${inc.place || "—"}</u>
                        </span>
                        <em className="ops-run__trail">${inc.status}</em>
                      </article>`,
                  )}
                </div>`
              : html`<p className="ops-hud__quiet ops-coc__quiet">No open incidents.</p>`}
          <//>`
        : null}

      ${rail === "watch"
        ? html`<${M.section}
            key="pane-watch"
            className="ops-run__pane ops-coc__pane"
            aria-label="Watch"
            initial=${{ opacity: 0, y: 6 }}
            animate=${{ opacity: 1, y: 0 }}
            transition=${SOFT}>
            <div className="ops-hud__lane-h">
              <p className="ops-hud__k"><s></s>${paneTitle}</p>
              <em className="ops-run__match">${coversN}</em>
            </div>
            <p className="ops-run__hint ops-coc__hint ops-coc__hint--tight">
              Preview only · no site video path
              ${nearN ? ` · ${nearN} near` : ""}
            </p>
            <div className="ops-run__mosaic ops-coc__mosaic ops-coc__mosaic--field" role="list" data-zone=${z ? "1" : "0"}>
              ${multi.length
                ? multi.map(
                    (cam, i) => html`
                      <${M.article}
                        key=${cam.id}
                        className="ops-run__tile ops-coc__tile ops-coc__tile--field"
                        role="listitem"
                        data-role=${cam.role}
                        data-zone-bind=${cam.zonePreferred ? "1" : "0"}
                        data-ranked=${cam.zonePreferred || cam.role === "primary" ? "1" : "0"}
                        initial=${{ opacity: 0, scale: 0.97 }}
                        animate=${{ opacity: 1, scale: 1 }}
                        transition=${{ ...SOFT, delay: Math.min(0.12, i * 0.04) }}>
                        <div className="ops-coc__tile-face ops-run__tile-face" aria-hidden="true">
                          <i className="ops-coc__tile-scan"></i>
                          <b>${cam.aspect || "view"}</b>
                          ${cam.zonePreferred
                            ? html`<u className="ops-coc__tile-badge">cover</u>`
                            : null}
                        </div>
                        <span className="ops-run__main">
                          <b>${cam.label}</b>
                          <u>${cam.zonePreferred ? "pref" : `${cam.distM}m`}</u>
                        </span>
                        <div className="ops-run__acts ops-coc__acts">
                          <button type="button" className="btn btn--xs btn--ghost"
                            onClick=${() => focusUnit(cam.id)}>Map</button>
                          <button type="button" className="btn btn--xs btn--ghost"
                            onClick=${() => snapCam(cam.id)}>Snap</button>
                        </div>
                      <//>`,
                  )
                : html`<div className="ops-hud__quiet ops-coc__empty ops-coc__empty--field">
                    <b>No covers</b>
                    · ${z
                      ? "Bind cameras to this volume, or reframe."
                      : "Covers light up with plant + focus."}
                    ${z
                      ? html`<button type="button" className="btn btn--sm" style=${{ marginTop: 8 }}
                          onClick=${() => go("/ops/zones")}>Zone covers</button>`
                      : null}
                  </div>`}
            </div>
          <//>`
        : null}

      ${rail === "drag"
        ? html`<${M.section}
            key="pane-drag"
            className="ops-run__pane ops-coc__pane ops-coc__pane--drag"
            aria-label="Send"
            initial=${{ opacity: 0, y: 6 }}
            animate=${{ opacity: 1, y: 0 }}
            transition=${SOFT}>
            <div className="ops-hud__lane-h">
              <p className="ops-hud__k"><s></s>${paneTitle}</p>
            </div>
            <p className="ops-run__hint">
              ${z
                ? `Drop near ${z.label} or free ground.`
                : "Drop on a pin or free ground."}
            </p>
            <${SoftDispatchBoard} fleet=${fleet} toast=${toast} />
          <//>`
        : null}

      ${rail === "log"
        ? html`<${M.section}
            key="pane-log"
            className="ops-run__pane ops-coc__pane"
            aria-label="Trace"
            initial=${{ opacity: 0, y: 6 }}
            animate=${{ opacity: 1, y: 0 }}
            transition=${SOFT}>
            <div className="ops-hud__lane-h">
              <p className="ops-hud__k"><s></s>${paneTitle}</p>
              <em className="ops-run__match">${log.length || 0}</em>
            </div>
            <div className="ops-run__log ops-coc__log" role="list">
              ${log.length
                ? log.slice(0, 24).map(
                    (row) => html`
                      <div key=${row.id} className="ops-run__log-row ops-coc__log-row" role="listitem">
                        <time>${row.ts}</time>
                        <span>${quietKind(row.kind)}</span>
                        <b>${quietSay(row.say)}</b>
                      </div>`,
                  )
                : html`<p className="ops-hud__quiet ops-coc__quiet">Empty until something runs.</p>`}
            </div>
          <//>`
        : null}
    </div>

    <footer className="ops-hud__foot ops-run__foot ops-coc__foot ops-coc__foot--field">
      <span className="ops-hud__foot-role" data-soft="1">practice · this host</span>
    </footer>
  </div>`;
}

function prettyStatus(st) {
  return operatorSay(st, String(st || "—").replace("_", " ").toLowerCase());
}

function quietKind(k) {
  const s = String(k || "");
  if (/HUMAN|STOP|OVERRIDE/i.test(s)) return "hold";
  if (/SNAP|EVID/i.test(s)) return "shot";
  if (/DISPATCH|ROUTE|MISSION/i.test(s)) return "job";
  return operatorSay(s, "Note");
}

function quietSay(say) {
  return String(say || "")
    .replace(/\bAI\b/gi, "system")
    .replace(/\bSoft theatre\b/gi, "session")
    .replace(/\bAI Soft\b/gi, "session");
}
